import { fuzzyScore } from './fuzzy-scorer'
import { charMask, maskOr, maskWithin } from './search-entry'
import { isLetterOrDigit, isLower, isUpper } from './search-text'
import { TYPO_MIN_TOKEN_LENGTH, typoVariants } from './typo-variants'

// EntryScorer.cs + QueryContext.cs, line for line. All tokens against one
// entry:
//   * AND: every token must score >= 0 or the entry is out.
//   * Two channels per token: the name, and the breadcrumb at 0.6x (so a
//     name match always beats a path match for the same token).
//   * Typo fallback: an unquoted token of 4+ chars that missed both channels
//     retries every edit-distance-1 variant, keeping the best, minus 2.
//   * Compactness: a match counts only when its letters sit close together,
//     or are all word starts (initials, "mrt" -> "Meeting Room Template").
//   * Quoted tokens are exact substrings.
// Highlights: each token's winning alignment, in the channel it matched.

const f = Math.fround

const PATH_SCORE_MULTIPLIER = f(0.6)
const TYPO_PENALTY = 2

const NONE = 0
const NAME = 1
const PATH = 2

const EMPTY = Object.freeze([])
const MISS = Object.freeze({ score: -1, nameIndices: EMPTY, pathIndices: EMPTY })

// QueryContext: per-query work done once for every entry.
export const queryContext = tokens => {
  const tokenMasks = tokens.map(t => charMask(t.text))
  const variants = tokens.map(t => (t.quoted || !t.text || t.text.length < TYPO_MIN_TOKEN_LENGTH ? [] : typoVariants(t.text)))
  const variantMasks = variants.map(list => list.map(v => charMask(v)))
  return { tokens, tokenMasks, variants, variantMasks }
}

const isWordStart = (target, targetLower, j) => {
  if (j <= 0) return true
  if (!isLetterOrDigit(targetLower[j - 1])) return true
  return target != null && j < target.length && isUpper(target[j]) && isLower(target[j - 1])
}

// A clean match may spread over the token's length plus max(2, length/2)
// chars; a typo variant must be one run starting at a word start; all-word-
// start matches (initials) always count.
export const isCompact = (matched, target, targetLower, tokenLength, typo) => {
  if (!matched || matched.length === 0) return true
  if (typo && !isWordStart(target, targetLower, matched[0])) return false
  if (matched.length === 1) return true

  const span = matched[matched.length - 1] - matched[0] + 1
  const limit = typo ? tokenLength : tokenLength + Math.max(2, Math.floor(tokenLength / 2))
  if (span <= limit) return true

  for (let k = 0; k < matched.length; k++) if (!isWordStart(target, targetLower, matched[k])) return false
  return true
}

const popcount = x => {
  let n = 0
  while (x) {
    x &= x - 1
    n++
  }
  return n
}

// A distance-1 variant keeps every token char but at most one substituted
// occurrence, so it can only match when at most one distinct token char is
// absent from name+path and it occurs once in the token.
const typoFallbackPossible = (token, tokenMask, entry, hasPath) => {
  const targetMask = hasPath ? maskOr(entry.nameMask, entry.pathMask) : entry.nameMask
  const absentLetters = tokenMask.letters & ~targetMask.letters
  const absentDigits = tokenMask.digits & ~targetMask.digits
  const absentCount = popcount(absentLetters) + popcount(absentDigits)
  if (absentCount === 0) return true
  if (absentCount >= 2) return false

  const absentChar = absentLetters ? String.fromCharCode(97 + Math.log2(absentLetters)) : String.fromCharCode(48 + Math.log2(absentDigits))
  let occurrences = 0
  for (let i = 0; i < token.length; i++) if (token[i] === absentChar && ++occurrences >= 2) return false
  return true
}

const sortedDistinct = buf => {
  if (buf.length === 0) return EMPTY
  buf.sort((a, b) => a - b)
  const out = [buf[0]]
  for (let i = 1; i < buf.length; i++) if (buf[i] !== out[out.length - 1]) out.push(buf[i])
  return out
}

// { score, nameIndices, pathIndices }; score < 0 is no match.
export const scoreEntry = (entry, tokens, now, context = null) => {
  if (!tokens || tokens.length === 0) return { score: 0, nameIndices: EMPTY, pathIndices: EMPTY }

  const nameBuf = []
  const pathBuf = []
  const hasPath = !!entry.lowerPath
  let total = 0

  for (let t = 0; t < tokens.length; t++) {
    const token = tokens[t]
    if (!token.text) return MISS

    const tokenMask = context ? context.tokenMasks[t] : charMask(token.text)

    let best = -1
    let bestChannel = NONE
    let bestIndices = null

    // 1) Name channel.
    if (maskWithin(tokenMask, entry.nameMask)) {
      const r = fuzzyScore(token.text, entry.displayName, entry.lowerName, entry.updatedAt, now, token.quoted)
      if (r.score > best && isCompact(r.indices, entry.displayName, entry.lowerName, token.text.length, false)) {
        best = r.score
        bestChannel = NAME
        bestIndices = r.indices
      }
    }

    // 2) Path channel, at 0.6x.
    if (hasPath && maskWithin(tokenMask, entry.pathMask)) {
      const r = fuzzyScore(token.text, entry.pathDisplay, entry.lowerPath, entry.updatedAt, now, token.quoted)
      if (r.score >= 0 && isCompact(r.indices, entry.pathDisplay, entry.lowerPath, token.text.length, false)) {
        const pathScore = f(r.score * PATH_SCORE_MULTIPLIER)
        if (pathScore > best) {
          best = pathScore
          bestChannel = PATH
          bestIndices = r.indices
        }
      }
    }

    // 3) Typo fallback.
    if (best < 0 && !token.quoted && token.text.length >= TYPO_MIN_TOKEN_LENGTH && typoFallbackPossible(token.text, tokenMask, entry, hasPath)) {
      let variants
      let variantMasks
      if (context) {
        variants = context.variants[t]
        variantMasks = context.variantMasks[t]
      } else {
        variants = typoVariants(token.text)
        variantMasks = variants.map(v => charMask(v))
      }

      for (let v = 0; v < variants.length; v++) {
        const vMask = variantMasks[v]
        if (maskWithin(vMask, entry.nameMask)) {
          const r = fuzzyScore(variants[v], entry.displayName, entry.lowerName, entry.updatedAt, now)
          if (r.score > best && isCompact(r.indices, entry.displayName, entry.lowerName, variants[v].length, true)) {
            best = r.score
            bestChannel = NAME
            bestIndices = r.indices
          }
        }
        if (hasPath && maskWithin(vMask, entry.pathMask)) {
          const r = fuzzyScore(variants[v], entry.pathDisplay, entry.lowerPath, entry.updatedAt, now)
          if (r.score >= 0 && isCompact(r.indices, entry.pathDisplay, entry.lowerPath, variants[v].length, true)) {
            const pathScore = f(r.score * PATH_SCORE_MULTIPLIER)
            if (pathScore > best) {
              best = pathScore
              bestChannel = PATH
              bestIndices = r.indices
            }
          }
        }
      }
      if (best >= 0) best = f(best - TYPO_PENALTY)
    }

    if (best < 0) return MISS // AND fails
    total = f(total + best)

    if (bestChannel === NAME && bestIndices) nameBuf.push(...bestIndices)
    else if (bestChannel === PATH && bestIndices) pathBuf.push(...bestIndices)
  }

  return { score: total, nameIndices: sortedDistinct(nameBuf), pathIndices: sortedDistinct(pathBuf) }
}
